import { LinearProgress } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DateComponent, DateVariant } from "components/base/DateComponent";
import ErrorBox from "components/error/ErrorBox";
import DataGridView from "components/grid/DataGridView";
import { Link as RouterLink } from "react-router-dom";
import { createRoute } from "utils/url";
import { useAllRateAgreements } from "./hooks/useAllRateAgreements";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
    gap: theme.spacing(1)
  },
  gridRoot: {
    "&.dx-widget .dx-datagrid .dx-datagrid-header-panel": {
      backgroundColor: "#F5F7F7"
    },
    "&.dx-widget .dx-datagrid .dx-toolbar": {
      backgroundColor: "#F5F7F7"
    },
    "&.dx-widget .dx-datagrid .dx-datagrid-headers .dx-datagrid-table .dx-row > td":
      {
        backgroundColor: "#E6E6E6",
        fontWeight: 600,
        fontSize: 12,
        color: "#1E1E1E",
        padding: 0,
        paddingLeft: theme.spacing(1),
        height: 24,
        borderRight: "solid 1px #aaa",
        borderLeft: "solid 1px #aaa"
      },
    "&.dx-widget .dx-datagrid .dx-row-lines > td": {
      border: "none",
      padding: "4px 8px",
      height: 40,
      color: "#464646",
      fontSize: 12,
      fontWeight: 400,
      textAlign: "left !important"
    },
    "&.dx-widget .dx-datagrid .dx-datagrid-rowsview .dx-row": {
      "&:hover": { backgroundColor: "#EEF6F8" }
    }
  },
  link: {
    color: "#0073C6",
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" }
  }
}));

interface RateAgreementRow {
  id: string;
  name: string;
  description: string | null;
  carrier: string;
  serviceMethod: string;
  rateTableCount: number;
  createdAt: Date | null;
  lastUpdatedBy: string | null;
  lastUpdatedAt: Date | null;
}

export default function RateAgreements() {
  const classes = useStyles();
  const { rateAgreements, loading, error } = useAllRateAgreements();

  const rows: RateAgreementRow[] = rateAgreements.map(ra => {
    const sorted = [...ra.auditTrail].sort(
      (a, b) =>
        new Date(a.changedAt.utcDateTime).getTime() -
        new Date(b.changedAt.utcDateTime).getTime()
    );
    const first = sorted[0] ?? null;
    const last = sorted[sorted.length - 1] ?? null;
    return {
      id: ra.id,
      name: ra.name,
      description: ra.description ?? null,
      carrier: ra.carrier,
      serviceMethod: ra.serviceMethod,
      rateTableCount: ra.rateTables.length,
      createdAt: first ? first.changedAt.utcDateTime : null,
      lastUpdatedBy: last ? last.changedBy : null,
      lastUpdatedAt: last ? last.changedAt.utcDateTime : null
    };
  });

  if (loading) return <LinearProgress />;
  if (error) return <ErrorBox apolloError={error} />;

  return (
    <div className={classes.root}>
      <DataGridView<RateAgreementRow>
        dataSource={rows}
        isLoading={false}
        showFilterRow={true}
        allowSelection={false}
        height="100%"
        gridClasses={{ root: classes.gridRoot }}
        columns={[
          {
            colDef: { dataField: "name", caption: "Name", allowSorting: true },
            render: ({ data }) => (
              <RouterLink
                to={createRoute(`/rate-agreements/${data.id}`)}
                className={classes.link}
              >
                {data.name}
              </RouterLink>
            )
          },
          {
            colDef: {
              dataField: "description",
              caption: "Description",
              allowSorting: false,
              width: 300
            }
          },
          {
            colDef: {
              dataField: "carrier",
              caption: "Carrier",
              allowSorting: true
            }
          },
          {
            colDef: {
              dataField: "serviceMethod",
              caption: "Service Method",
              allowSorting: true
            }
          },
          {
            colDef: {
              dataField: "rateTableCount",
              caption: "# Rate Tables",
              allowSorting: true,
              alignment: "right",
              width: 120
            }
          },
          {
            colDef: {
              dataField: "createdAt",
              caption: "Created At",
              allowSorting: true,
              width: 180
            },
            render: ({ data }) => (
              <span data-testid={`created-at-${data.id}`}>
                {data.createdAt ? (
                  <DateComponent
                    date={data.createdAt}
                    variant={DateVariant.SHORT_WITH_TIME}
                  />
                ) : (
                  "\u2014"
                )}
              </span>
            )
          },
          {
            colDef: {
              dataField: "lastUpdatedBy",
              caption: "Last Updated By",
              allowSorting: true
            }
          },
          {
            colDef: {
              dataField: "lastUpdatedAt",
              caption: "Last Updated At",
              allowSorting: true,
              width: 180
            },
            render: ({ data }) => (
              <span data-testid={`updated-at-${data.id}`}>
                {data.lastUpdatedAt ? (
                  <DateComponent
                    date={data.lastUpdatedAt}
                    variant={DateVariant.SHORT_WITH_TIME}
                  />
                ) : (
                  "\u2014"
                )}
              </span>
            )
          }
        ]}
        data-testid="rate-agreements-grid"
      />
    </div>
  );
}
